// src/App.js
import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Provider } from "./components/ui/provider";
import { Box, Container } from "@chakra-ui/react";
import MainPage from "./pages/MainPage";
import DeliveryPage from "./pages/DeliveryPage";
import DeliveryQrRedirect from "./pages/DeliveryQrRedirect";
import { messaging, messagingSupported, getInitializationPromise } from "./firebase";
import { onMessage } from "firebase/messaging";
import { isBusy, onIdle } from "./utils/appBusy";

function App() {
  useEffect(() => {
    // Firebase Messaging 초기화만 수행 (권한 요청은 하지 않음)
    const setupMessaging = async () => {
      try {
        // 초기화 완료 대기
        await getInitializationPromise();
        console.log("✅ Firebase Messaging 초기화 완료 (권한 요청 없이)");

        // Firebase Messaging이 지원되는 경우에만 메시지 리스너 설정
        if (messagingSupported && messaging) {
          // 권한 상태와 무관하게 등록한다. 마운트 시점의 권한만 보고 건너뛰면,
          // 기사님이 앱 안에서 알림설정을 눌러 권한을 허용해도 이 이펙트는 다시
          // 실행되지 않아 그 세션 내내 포그라운드 알림이 도착하지 않는다.
          // onMessage 등록 자체는 권한을 필요로 하지 않는다.
          console.log("📱 포그라운드 메시지 리스너 설정");

          // 앱 실행 중 푸시 알림 수신
          const unsubscribe = onMessage(messaging, (payload) => {
            console.log("Message received. ", payload);
            if (payload.notification) {
              alert(payload.notification.title + " - " + payload.notification.body);
            }
          });

          // cleanup function 반환
          return unsubscribe;
        } else {
          console.log("Firebase Messaging을 사용할 수 없는 환경입니다.");
        }
      } catch (error) {
        console.error("Firebase Messaging 설정 중 오류:", error);
      }
    };

    // setupMessaging은 비동기라 정리 함수가 먼저 실행될 수 있다. 그때는
    // unsubscribe가 아직 null이라 리스너가 해제되지 않고 남는다.
    let cancelled = false;
    let unsubscribe = null;
    setupMessaging().then((unsub) => {
      if (cancelled) {
        if (typeof unsub === "function") {
          unsub();
        }
        return;
      }
      unsubscribe = unsub;
    });

    // cleanup function

    // 새 서비스워커가 활성화됐을 때의 새로고침 처리.
    // 배송 사진을 촬영했거나 전송 중인 기사님의 화면을 그대로 날려버리지 않도록,
    // 진행 중인 작업이 끝난 뒤에 새로고침한다.
    let reloadScheduled = false;
    let stopWaitingForIdle = null;

    const reloadWhenIdle = () => {
      if (reloadScheduled) {
        return;
      }
      reloadScheduled = true;

      if (!isBusy()) {
        window.location.reload();
        return;
      }

      console.log("⏸️ 진행 중인 작업이 있어 새로고침을 보류합니다.");
      stopWaitingForIdle = onIdle(() => {
        stopWaitingForIdle();
        stopWaitingForIdle = null;
        window.location.reload();
      });
    };

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          // 새 서비스워커가 waiting 상태면 skipWaiting 메시지 전송
          registration.waiting.postMessage({ type: "SKIP_WAITING" });

          // 새 서비스워커 활성화 후 새로고침
          registration.waiting.addEventListener("statechange", (e) => {
            console.log("새 서비스워커 상태 변화:", e.target.state);
            if (e.target.state === "activated") {
              reloadWhenIdle();
            }
          });
        }
      });

      // 서비스워커 업데이트 감지 (앱 실행 중에도)
      navigator.serviceWorker.addEventListener("controllerchange", reloadWhenIdle);
    }

    return () => {
      cancelled = true;
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }

      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("controllerchange", reloadWhenIdle);
      }

      if (stopWaitingForIdle) {
        stopWaitingForIdle();
        stopWaitingForIdle = null;
      }
    };
  }, []);

  return (
    <Provider>
      <Box bg="#f6f6f6" minHeight="100vh" width="100%">
        <Container p={0} maxW="500px" mx="auto" minHeight="100vh" display="flex" flexDirection="column" bg="white">
          <Router>
            <Routes>
              <Route path="/" element={<MainPage />} />
              <Route path="/delivery" element={<DeliveryPage />} />
              {/* 영수증 QR 진입점. /delivery?delivery_id=... 로 넘긴다 */}
              <Route path="/d/:deliveryId" element={<DeliveryQrRedirect />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </Container>
      </Box>
    </Provider>
  );
}

export default App;
